/**
 * Upcoming Charges (DNKADM-0145) status chip tint pairs (DNKADM-0269) — moved
 * here from `src/features/pending-charges/UpcomingChargesScreen.tsx` so the
 * pairs are mode-aware and the feature dir stays literal-free (the DNKADM-0269
 * ESLint guard bans raw colors in `src/features/**`; `src/theme/**` owns them).
 *
 * LIGHT values are byte-identical to the pre-0269 literals (the 0266 identity
 * constraint). Dark values follow the same translucent-tint + lifted-ink ramp
 * as the rest of the sweep, reusing the matching `STATUS_DARK` family in
 * `../tokens.ts` where a status shares its meaning (`charged` ~ active,
 * `failed` ~ cancelled/error); `pending`'s violet has no existing twin in
 * `tokens.ts`, so it gets its own pastel on the same ramp.
 */

/** One status chip's tint/ink pair. */
interface PendingChargeTint {
  bg: string;
  fg: string;
}

export interface PendingChargesMaps {
  status: Record<'pending' | 'charged' | 'cancelled' | 'failed', PendingChargeTint>;
  /** Fallback for a status outside the known enum. */
  neutral: PendingChargeTint;
}

const NEUTRAL_LIGHT: PendingChargeTint = { bg: '#EDEAE4', fg: 'rgba(0,0,0,0.6)' };
// Dark neutral fg mirrors `inkAlpha(theme, 0.6)` in dark mode (the dark ink
// family at the same alpha as the light `rgba(0,0,0,0.6)` literal).
const NEUTRAL_DARK: PendingChargeTint = { bg: 'rgba(105,114,108,0.22)', fg: 'rgba(233,243,236,0.6)' };

const LIGHT: PendingChargesMaps = {
  status: {
    pending: { bg: '#EDEAF6', fg: '#5B4B9E' },
    charged: { bg: '#E5F1E9', fg: '#2E7D52' },
    cancelled: { ...NEUTRAL_LIGHT },
    failed: { bg: '#F6E4E0', fg: '#C0492E' },
  },
  neutral: NEUTRAL_LIGHT,
};

const DARK: PendingChargesMaps = {
  status: {
    pending: { bg: 'rgba(91,75,158,0.24)', fg: '#B8AFDC' },
    charged: { bg: 'rgba(46,157,94,0.18)', fg: '#7FD3A3' },
    cancelled: { ...NEUTRAL_DARK },
    failed: { bg: 'rgba(192,73,46,0.18)', fg: '#F0A090' },
  },
  neutral: NEUTRAL_DARK,
};

/** Per-mode pending-charges status tints. Light === the pre-0269 literals. */
export function buildPendingChargesMaps(mode: 'light' | 'dark'): PendingChargesMaps {
  return mode === 'dark' ? DARK : LIGHT;
}
